import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { jobsService, JOBS_QUERY_KEY } from '@/features/jobs';
import type { UrlCheckResult, StartTranslationResponse } from '@/features/jobs';

type TranslationStep = 'form' | 'selection' | 'progress' | 'results';

interface TranslationState {
  step: TranslationStep;
  urls: string[];
  targetLang: string;
  urlCheckResults: UrlCheckResult[];
  selectedUrls: string[];
  jobId: string | null;
}

export function useTranslationForm() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<TranslationState>({
    step: 'form',
    urls: [],
    targetLang: '',
    urlCheckResults: [],
    selectedUrls: [],
    jobId: null,
  });

  const checkMutation = useMutation({
    mutationFn: ({ urls, targetLang }: { urls: string[]; targetLang: string }) =>
      jobsService.checkTranslations(urls, targetLang),
    onSuccess: (data, variables) => {
      // Pre-select URLs that are not already translated
      const preSelected = data.results
        .filter((r) => !r.isTranslated && !r.error)
        .map((r) => r.url);

      setState((prev) => ({
        ...prev,
        step: 'selection',
        urls: variables.urls,
        targetLang: variables.targetLang,
        urlCheckResults: data.results,
        selectedUrls: preSelected,
      }));
    },
  });

  const translateMutation = useMutation({
    mutationFn: (data: { urls: string[]; targetLang: string }) =>
      jobsService.startTranslation(data),
    onSuccess: (data: StartTranslationResponse) => {
      setState((prev) => ({
        ...prev,
        step: 'progress',
        jobId: data.jobId,
      }));
      // Invalidate jobs list
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
    },
  });

  const submitForm = useCallback((urlsText: string, targetLang: string) => {
    const urls = urlsText
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urls.length === 0) return;

    checkMutation.mutate({ urls, targetLang });
  }, [checkMutation]);

  const toggleUrl = useCallback((url: string) => {
    setState((prev) => ({
      ...prev,
      selectedUrls: prev.selectedUrls.includes(url)
        ? prev.selectedUrls.filter((u) => u !== url)
        : [...prev.selectedUrls, url],
    }));
  }, []);

  const selectAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedUrls: prev.urlCheckResults
        .filter((r) => !r.error)
        .map((r) => r.url),
    }));
  }, []);

  const deselectAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedUrls: [],
    }));
  }, []);

  const startTranslation = useCallback(() => {
    if (state.selectedUrls.length === 0) return;
    translateMutation.mutate({
      urls: state.selectedUrls,
      targetLang: state.targetLang,
    });
  }, [state.selectedUrls, state.targetLang, translateMutation]);

  const cancel = useCallback(() => {
    setState({
      step: 'form',
      urls: [],
      targetLang: '',
      urlCheckResults: [],
      selectedUrls: [],
      jobId: null,
    });
  }, []);

  const goToResults = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'results',
    }));
  }, []);

  const reset = useCallback(() => {
    setState({
      step: 'form',
      urls: [],
      targetLang: '',
      urlCheckResults: [],
      selectedUrls: [],
      jobId: null,
    });
  }, []);

  return {
    ...state,
    isCheckingUrls: checkMutation.isPending,
    isStartingTranslation: translateMutation.isPending,
    checkError: checkMutation.error,
    translateError: translateMutation.error,
    submitForm,
    toggleUrl,
    selectAll,
    deselectAll,
    startTranslation,
    cancel,
    goToResults,
    reset,
  };
}
